import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, DeviceMobile, EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login, sendPhoneOtp, verifyPhoneOtp } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("method") === "phone" ? "phone" : "email");

  return (
    <div className="min-h-screen relative overflow-hidden grain bg-ink flex items-center justify-center px-5 py-10">
      <div className="absolute inset-0 opacity-50">
        <img src="https://images.unsplash.com/photo-1657450717623-93270c89af8e?fm=jpg&q=85&w=1600&auto=format&fit=crop" alt="Lignum Vitae — Jamaica's national flower" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/75 to-ink" />
      </div>
      <div className="relative z-10 w-full max-w-md crystal-glass rounded-3xl p-8 sm:p-10 animate-fade-up overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-2 flex flex-col" aria-hidden>
          <div className="flex-1 bg-jam-black" />
          <div className="flex-1 bg-jam-green" />
          <div className="flex-1 bg-jam-gold" />
        </div>
        <div className="absolute right-2 top-8 bottom-8 w-px bg-cream/10" aria-hidden />
        <div className="flex items-center gap-3 mb-3">
          <p className="text-papaya text-xs tracking-[0.3em] uppercase font-bold">Giffwurldaunce</p>
          <div className="jam-stripe h-[3px] flex-1 rounded-full" aria-hidden />
        </div>
        <h1 className="text-cream text-4xl sm:text-5xl font-black tracking-tighter leading-none">Welcome<br/>back home.</h1>
        <p className="text-cream/60 mt-3 text-sm">Log in and feel the rhythm of the islands. 🇯🇲</p>

        {mode === "email" ? (
          <EmailLogin login={login} onDone={() => nav("/")} onPhone={() => setMode("phone")} />
        ) : (
          <PhoneLogin
            sendPhoneOtp={sendPhoneOtp}
            verifyPhoneOtp={verifyPhoneOtp}
            onNew={() => nav("/welcome", { replace: true })}
            onReturning={() => nav("/")}
            onEmail={() => setMode("email")}
          />
        )}

        <p className="mt-6 text-cream/60 text-sm text-center">
          New here?{" "}
          <Link to="/signup" data-testid="login-to-signup" className="text-papaya font-bold hover:underline">Create an account</Link>
        </p>
      </div>
    </div>
  );
}

function EmailLogin({ login, onDone, onPhone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      onDone();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="Email or username" type="text" value={email} onChange={setEmail} testid="login-email" />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
        {err && <p data-testid="login-error" className="text-red-400 text-sm">{err}</p>}
        <button
          data-testid="login-submit"
          type="submit"
          disabled={loading}
          className="w-full mt-4 py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? "Logging in…" : <>Enter the wave <ArrowRight size={16} weight="bold" /></>}
        </button>
        <div className="mt-4 flex flex-col items-center gap-2">
          <Link to="/recover" data-testid="login-forgot-password" className="text-papaya text-sm font-bold hover:underline">Forgot Password?</Link>
          <Link to="/recover-id" data-testid="login-forgot-identifier" className="text-cream/80 text-sm font-bold hover:text-papaya hover:underline">Forgot Email or Phone?</Link>
        </div>
      </form>
      <Divider />
      <button
        data-testid="login-phone-toggle"
        onClick={onPhone}
        className="w-full py-3.5 rounded-full border border-cream/20 text-cream font-bold text-sm hover:bg-cream/5 transition-colors flex items-center justify-center gap-2"
      >
        <DeviceMobile size={18} weight="bold" /> Continue with phone
      </button>
    </>
  );
}

function PhoneLogin({ sendPhoneOtp, verifyPhoneOtp, onNew, onReturning, onEmail }) {
  const [step, setStep] = useState("number"); // number | code
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const doSend = async () => {
    setErr(""); setLoading(true);
    try {
      const res = await sendPhoneOtp(phone.trim());
      setStep("code");
      setCooldown(res?.resend_in || 30);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const send = (e) => { e.preventDefault(); doSend(); };

  const verify = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const res = await verifyPhoneOtp(phone.trim(), code.trim());
      if (res.is_new_user) onNew(); else onReturning();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (step === "number") {
    return (
      <>
        <form onSubmit={send} className="mt-8 space-y-4">
          <Field
            label="Phone number"
            type="tel"
            value={phone}
            onChange={setPhone}
            testid="login-phone-input"
            placeholder="+1 876 555 1234"
          />
          <p className="text-cream/40 text-xs">Include your country code (e.g. +1 876 for Jamaica).</p>
          {err && <p data-testid="login-phone-error" className="text-red-400 text-sm">{err}</p>}
          <button
            data-testid="login-phone-send"
            type="submit"
            disabled={loading}
            className="w-full mt-2 py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? "Sending…" : <>Send code <ArrowRight size={16} weight="bold" /></>}
          </button>
        </form>
        <Divider />
        <button
          data-testid="login-email-toggle"
          onClick={onEmail}
          className="w-full py-3.5 rounded-full border border-cream/20 text-cream font-bold text-sm hover:bg-cream/5 transition-colors flex items-center justify-center gap-2"
        >
          <EnvelopeSimple size={18} weight="bold" /> Use email instead
        </button>
      </>
    );
  }

  return (
    <form onSubmit={verify} className="mt-8 space-y-4">
      <button
        type="button"
        data-testid="login-phone-back"
        onClick={() => { setStep("number"); setErr(""); setCode(""); }}
        className="text-cream/50 text-xs flex items-center gap-1 hover:text-papaya"
      >
        <ArrowLeft size={14} weight="bold" /> Change number
      </button>
      <p className="text-cream/70 text-sm">Enter the 6-digit code sent to <span className="text-papaya font-bold">{phone}</span>.</p>
      <Field
        label="Verification code"
        type="text"
        value={code}
        onChange={(v) => setCode(v.replace(/[^0-9]/g, ""))}
        testid="login-otp-input"
        placeholder="123456"
        inputMode="numeric"
      />
      {err && <p data-testid="login-otp-error" className="text-red-400 text-sm">{err}</p>}
      <button
        data-testid="login-otp-verify"
        type="submit"
        disabled={loading}
        className="w-full mt-2 py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? "Verifying…" : <>Verify & continue <ArrowRight size={16} weight="bold" /></>}
      </button>
      <button
        type="button"
        data-testid="login-otp-resend"
        onClick={doSend}
        disabled={loading || cooldown > 0}
        className="w-full text-center text-cream/60 text-xs hover:text-papaya disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't get it? Resend code"}
      </button>
    </form>
  );
}

function Divider() {
  return (
    <div className="my-5 flex items-center gap-3" aria-hidden>
      <div className="flex-1 h-px bg-cream/10" />
      <span className="text-cream/30 text-[10px] tracking-[0.25em] uppercase">or</span>
      <div className="flex-1 h-px bg-cream/10" />
    </div>
  );
}

function Field({ label, type, value, onChange, testid, placeholder, inputMode }) {
  return (
    <label className="block">
      <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        required
        className="mt-2 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya transition-colors"
      />
    </label>
  );
}
